import { useCallback, Dispatch, SetStateAction } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useUserRegisterByCustomRegister,
  useUserLogin,
} from "../../../../hooks/users";
import {
  SocialAuthResponse,
  RegistrationType,
  RegistrationStep,
} from "../types";
import { LOGIN_REFUSALS, SIGN_UP_REFUSALS } from "../registrationRefusals";
import {
  refusalMessageKey,
  refusedFields,
} from "../../../../utils/refusalMessageKey";

const UNEXPLAINED_REFUSAL = "customRegistration.page.toast.unexpectedError";

interface UseEmailAuthProps {
  entityIdentification?: string;
  form_id?: string;
  setCurrentStep: Dispatch<SetStateAction<RegistrationStep>>;
  setType: Dispatch<SetStateAction<RegistrationType>>;
  setSocialResponse: Dispatch<SetStateAction<SocialAuthResponse | null>>;
  setEmail: (email: string) => void;
  getStepNumber: (
    logicalStep: "auth" | "form" | "questions"
  ) => RegistrationStep;
  requirePassword: boolean;
}

interface EmailAuthData {
  email: string;
  password?: string;
}

export const useEmailAuth = ({
  entityIdentification,
  form_id,
  setCurrentStep,
  setType,
  setSocialResponse,
  setEmail,
  getStepNumber,
  requirePassword,
}: UseEmailAuthProps) => {
  const { t } = useTranslation("clientView");
  const { mutate: registerUser } = useUserRegisterByCustomRegister();
  const { mutate: loginUser } = useUserLogin();

  const handleEmailRegister = useCallback(
    (
      data: EmailAuthData,
      onEmailVerificationNeeded: (userId: string) => void,
      onAlreadyExists?: (email: string, password?: string) => void,
      /** Lets the page explain a refused password, or a refusal that only has the generic text; returns whether it did. */
      explainRefusal?: () => boolean
    ) =>
      new Promise<void>((answered) => {
        const registrationData: any = {
          user_email: data.email,
          user_password: data.password,
          user_confirm_password: data.password,
        };

        registerUser(
          {
            entity_identification: entityIdentification,
            form_id,
            data: {
              data: registrationData,
            },
          },
          {
            onSettled: () => answered(),
            onSuccess: (response) => {
              if (response.status === 201 || response.status == 200) {
                const element = response.data.element;
                const userId = element?.user_identification;

                if (element?.user_is_active) {
                  if (onAlreadyExists) {
                    onAlreadyExists(
                      data.email,
                      requirePassword ? data.password : undefined
                    );
                  }
                } else {
                  onEmailVerificationNeeded(userId);
                }
              } else {
                toast.error(t("customRegistration.page.toast.unexpectedError"));
              }
            },
            onError: (error: any) => {
              const isAlreadyComplete =
                error.response?.data?.code === "REGISTRATION_ALREADY_COMPLETE";
              const isAlreadyExists =
                error.response?.data?.code === "REGISTER_USER_EXISTS" ||
                error.response?.status === 409;

              if (isAlreadyComplete) {
                toast.error(
                  t("customRegistration.page.toast.registrationAlreadyComplete")
                );
              } else if (isAlreadyExists && onAlreadyExists) {
                onAlreadyExists(
                  data.email,
                  requirePassword ? data.password : undefined
                );
              } else {
                const message = refusalMessageKey(
                  error,
                  SIGN_UP_REFUSALS,
                  UNEXPLAINED_REFUSAL
                );
                const explainable =
                  message === UNEXPLAINED_REFUSAL ||
                  refusedFields(error).includes("user_password");
                if (!explainable || !explainRefusal?.()) {
                  toast.error(t(message));
                }
              }
            },
          }
        );
      }),
    [
      entityIdentification,
      form_id,
      registerUser,
      requirePassword,
      t,
      getStepNumber,
      setCurrentStep,
      setType,
      setSocialResponse,
      setEmail,
    ]
  );

  const handleEmailLogin = useCallback(
    (email: string, password: string) => {
      loginUser(
        {
          entity_identification: entityIdentification,
          data: {
            user_email: email,
            user_password: password,
          },
        },
        {
          onSuccess: (response) => {
            if (response.status === 200 && response.data.element) {
              const element = response.data.element;

              if (element.user_is_active) {
                const nextStep = getStepNumber("form");
                setCurrentStep(nextStep);
                setType("email");
                setSocialResponse({
                  user_identification: element.user_identification,
                  access_token: element.access_token,
                  user_email: email,
                });
                setEmail(email);
              } else {
                toast.warn(t("customRegistration.page.toast.emailNotVerified"));
              }
            } else {
              toast.warn(t("customRegistration.page.toast.unexpectedError"));
            }
          },
          onError: (err: any) => {
            console.error("Email login error:", err);
            toast.error(
              t(
                refusalMessageKey(
                  err,
                  LOGIN_REFUSALS,
                  "customRegistration.page.toast.unexpectedError"
                )
              )
            );
          },
        }
      );
    },
    [
      entityIdentification,
      loginUser,
      setCurrentStep,
      setType,
      setSocialResponse,
      setEmail,
      getStepNumber,
      t,
    ]
  );

  return {
    handleEmailRegister,
    handleEmailLogin,
  };
};
